import type { GemxQlQueryResponse } from '~/core/gemxql/graphql';
import type { ETimeDimension } from '~/core/gemxql/types';
import type { IAnalyticMetric } from '~/types/metric';

import { EXPERIMENT_FIELD } from '../constants/campaign-metrics-v2';
import { buildAnalyticMetric } from './buildAnalyticMetric';
import { extractQueryRows } from '~/core/gemxql/helpers';
import { IVariantType } from '~/modules/campaign/types';
import { GAP_ROW_KEY } from '~/core/gemxql/constants';
import { ERowReaderMode } from '~/core/gemxql/types';
import { isNullish } from './isNullish';

export interface IExtractCampaignGroupDetailDataParams {
  response?: GemxQlQueryResponse;
  interval?: ETimeDimension;
}

export interface IExtractCampaignGroupDetailDataResult {
  totalsByVariant: Partial<Record<IVariantType, IAnalyticMetric>>;
  intervalByVariant: Partial<Record<IVariantType, IAnalyticMetric[]>>;
  unknownVersions: string[];
}

const VARIANT_TYPES: string[] = [IVariantType.VARIANT_A, IVariantType.VARIANT_B];

export const extractCampaignGroupDetailData = ({
  response,
  interval,
}: IExtractCampaignGroupDetailDataParams): IExtractCampaignGroupDetailDataResult => {
  const rows = extractQueryRows(response);
  if (!rows.length) return { totalsByVariant: {}, intervalByVariant: {}, unknownVersions: [] };

  const intervalByVariant: Partial<Record<IVariantType, IAnalyticMetric[]>> = {};
  const totalsByVariant: Partial<Record<IVariantType, IAnalyticMetric>> = {};
  const unknownVersionSet = new Set<string>();

  for (const row of rows) {
    const versionRaw = row[EXPERIMENT_FIELD.VERSION];
    if (isNullish(versionRaw)) continue;
    const version = String(versionRaw);
    if (!VARIANT_TYPES.includes(version)) {
      unknownVersionSet.add(version);
      continue;
    }
    const variantType = version as IVariantType;

    const intervalValueRaw = interval ? row[interval] : undefined;
    const date = !isNullish(intervalValueRaw) ? String(intervalValueRaw) : '';

    const metric = buildAnalyticMetric({
      row,
      readerMode: ERowReaderMode.DEFAULT,
      date,
      variantKey: variantType,
    });

    const bucket = intervalByVariant[variantType] ?? [];
    bucket.push(metric);
    intervalByVariant[variantType] = bucket;

    // Without a time dimension each row already IS that side's total. With one, the side is split
    // across buckets, so read the server's subtotal (WITH GROUP_TOTALS) instead of summing them —
    // distinct metrics can't be added up. Gap-filled rows zero those columns.
    if (!totalsByVariant[variantType] && !row[GAP_ROW_KEY]) {
      totalsByVariant[variantType] = interval
        ? buildAnalyticMetric({
            row,
            readerMode: ERowReaderMode.GROUP_TOTALS,
            groupDimension: EXPERIMENT_FIELD.VERSION,
            date: '',
            variantKey: variantType,
          })
        : metric;
    }
  }

  return { totalsByVariant, intervalByVariant, unknownVersions: [...unknownVersionSet] };
};
